// Single pagination control used by every list view (Logs, Users, API
// Keys). Server-side pagination is the contract: parent owns `total`,
// `offset`, `limit` and reloads on `onChange(newOffset)`. The component
// is purely presentational — no data fetching.
//
// Layout matches the existing Logs footer that this component replaced:
// "Showing N–M of T" on the left, first / prev / next / last on the
// right. Buttons gracefully disable at the edges.
export default function Pagination({ total = 0, offset = 0, limit = 50, onChange }) {
  const safeLimit = Math.max(1, limit | 0);
  const lastOffset = Math.max(0, Math.floor(Math.max(0, total - 1) / safeLimit) * safeLimit);

  const pageFrom = total === 0 ? 0 : offset + 1;
  const pageTo   = Math.min(total, offset + safeLimit);
  const hasPrev  = offset > 0;
  const hasNext  = offset + safeLimit < total;

  const go = (next) => {
    const clamped = Math.max(0, Math.min(lastOffset, next));
    if (clamped !== offset) onChange?.(clamped);
  };

  return (
    <div className="flex items-center justify-between border-t border-ink-100 px-4 py-3 text-xs text-ink-500">
      <div>
        {total === 0
          ? <span>No items</span>
          : <>Showing <b>{pageFrom}</b>–<b>{pageTo}</b> of <b>{total.toLocaleString('en-US')}</b></>}
      </div>
      <div className="flex gap-1">
        <button
          type="button"
          className="btn-ghost p-2 disabled:opacity-40"
          disabled={!hasPrev}
          onClick={() => go(0)}
          title="First page"
        >
          <i className="fa-solid fa-angles-left" />
        </button>
        <button
          type="button"
          className="btn-ghost p-2 disabled:opacity-40"
          disabled={!hasPrev}
          onClick={() => go(offset - safeLimit)}
          title="Previous page"
        >
          <i className="fa-solid fa-chevron-left" />
        </button>
        <button
          type="button"
          className="btn-ghost p-2 disabled:opacity-40"
          disabled={!hasNext}
          onClick={() => go(offset + safeLimit)}
          title="Next page"
        >
          <i className="fa-solid fa-chevron-right" />
        </button>
        <button
          type="button"
          className="btn-ghost p-2 disabled:opacity-40"
          disabled={!hasNext}
          onClick={() => go(lastOffset)}
          title="Last page"
        >
          <i className="fa-solid fa-angles-right" />
        </button>
      </div>
    </div>
  );
}

// Default page size used by every list. Centralised so changing the
// system-wide pagination is a one-line edit.
export const PAGE_SIZE = 50;
